import type { MpRequestStatus } from '@/lib/apiClient';

/**
 * The manpower request lifecycle, client side (#303 slice 3).
 *
 * ⚠️ A MIRROR of apps/api/src/manpower/lib/request-lifecycle.ts — the API is
 * the enforcement; this only lets the form offer the right moves and refuse the
 * obvious ones before the round trip. Keep the two tables identical by eye.
 *
 *   received ─┬─▶ reviewed ─┬─▶ booked   ─┐
 *             │             └─▶ declined ─┤
 *             ├─▶ booked                  ├─▶ reviewed   (reopen)
 *             └─▶ declined                ┘
 *   archived   (imported history — untouchable)
 *
 * "Booked" RECORDS that the scheduler has already entered the crew on the grid
 * by hand. Nothing here, or on the API, writes the grid (operations, 2026-08-31).
 */

export type TriageTarget = 'reviewed' | 'booked' | 'declined';

const NEXT: Record<MpRequestStatus, readonly TriageTarget[]> = {
  received: ['reviewed', 'booked', 'declined'],
  reviewed: ['booked', 'declined'],
  booked:   ['reviewed'],
  declined: ['reviewed'],
  archived: [],
};

export const STATUS_LABELS: Record<MpRequestStatus, string> = {
  received: 'Received',
  reviewed: 'Reviewed',
  booked:   'Booked',
  declined: 'Declined',
  archived: 'Archived',
};

/** The moves a scheduler may make from here. Empty for archived. */
export function triageTargets(from: MpRequestStatus): readonly TriageTarget[] {
  return NEXT[from] ?? [];
}

export function isReopen(from: MpRequestStatus, to: TriageTarget): boolean {
  return (from === 'booked' || from === 'declined') && to === 'reviewed';
}

/** Declining and reopening both need words — the PM reads them without the conversation. */
export function noteRequired(from: MpRequestStatus, to: TriageTarget): boolean {
  return to === 'declined' || isReopen(from, to);
}

/** What the radio says for a move, in the scheduler's words. */
export function targetLabel(from: MpRequestStatus, to: TriageTarget): string {
  if (isReopen(from, to)) return 'Reopen for review';
  switch (to) {
    case 'reviewed': return 'Mark reviewed — talking it through with the PM';
    case 'booked':   return 'Booked — the crew is on the grid';
    case 'declined': return 'Decline';
  }
}

/**
 * Client-side check mirroring the API's refusals. Returns the message to show,
 * or null when the move may be sent.
 */
export function validateTriage(from: MpRequestStatus, to: TriageTarget, note: string): string | null {
  if (!triageTargets(from).includes(to)) {
    return `A ${STATUS_LABELS[from].toLowerCase()} request cannot be marked ${STATUS_LABELS[to].toLowerCase()}.`;
  }
  if (noteRequired(from, to) && !note.trim()) {
    return to === 'declined'
      ? 'Say why — the PM reads this without the conversation.'
      : 'Say what changed — the PM reads this without the conversation.';
  }
  return null;
}

/**
 * The count shown on the Requests toggle. Null hides the badge entirely — a
 * "0" pill would draw the eye to nothing. Capped so the toggle cannot stretch.
 */
export function openCountLabel(n: number | null | undefined): string | null {
  if (!n || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}
